/**
 * Product gallery: thumbnail switching, pointer-tracking zoom, keyboard
 * navigation and swipe on touch.
 */

import { $, $$, on } from '@/lib/dom'

export function initGallery(): void {
  const gallery = $('[data-gallery]')
  if (!gallery) return

  const main = $<HTMLElement>('[data-gallery-main]', gallery)
  const slides = $$('[data-gallery-slide]', gallery)
  const thumbs = $$('[data-gallery-thumb]', gallery)
  if (!main || slides.length === 0) return

  let index = 0

  function show(next: number): void {
    index = (next + slides.length) % slides.length

    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index))
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)))
  }

  thumbs.forEach((thumb, i) => {
    on(thumb, 'click', () => show(i))
  })

  // ---- Keyboard -----------------------------------------------------------
  // The gallery is focusable so arrow keys work without a pointer.
  on(gallery, 'keydown', (event) => {
    const key = event.key
    if (key === 'ArrowRight') show(index + 1)
    if (key === 'ArrowLeft') show(index - 1)
  })

  // ---- Zoom ---------------------------------------------------------------
  // Position is written to CSS custom properties and the actual scaling is
  // done in CSS — so the transform stays on the compositor and never triggers
  // a layout on pointer move.
  on(main, 'pointerenter', () => main.classList.add('is-zoomed'))
  on(main, 'pointerleave', () => {
    main.classList.remove('is-zoomed')
    main.style.removeProperty('--zoom-x')
    main.style.removeProperty('--zoom-y')
  })

  on(main, 'pointermove', (event) => {
    const e = event
    // Touch pointers should scroll and swipe, not zoom.
    if (e.pointerType === 'touch') return

    const rect = main.getBoundingClientRect()
    main.style.setProperty('--zoom-x', `${((e.clientX - rect.left) / rect.width) * 100}%`)
    main.style.setProperty('--zoom-y', `${((e.clientY - rect.top) / rect.height) * 100}%`)
  })

  // ---- Swipe --------------------------------------------------------------
  let startX = 0
  let tracking = false

  on(main, 'pointerdown', (event) => {
    const e = event
    if (e.pointerType !== 'touch') return
    startX = e.clientX
    tracking = true
  })

  on(main, 'pointerup', (event) => {
    if (!tracking) return
    tracking = false

    const delta = event.clientX - startX
    // 45px threshold: far enough not to fire on a tap, short enough to feel
    // responsive to a deliberate flick.
    if (Math.abs(delta) > 45) show(delta < 0 ? index + 1 : index - 1)
  })

  show(0)
}
